import { subYears } from 'date-fns';
import firebase from 'firebase/app';
import 'firebase/firestore';
import {
  Checkbox,
  ComboBox,
  DatePicker,
  DayOfWeek,
  DefaultButton,
  DetailsListLayoutMode,
  Dialog,
  DialogFooter,
  MarqueeSelection,
  MaskedTextField,
  Modal,
  PrimaryButton,
  ProgressIndicator,
  Selection,
  Shimmer,
  ShimmeredDetailsList,
  Text,
  TextField,
} from 'office-ui-fabric-react/lib';
import { PropTypes } from 'prop-types';
import React, { Component } from 'react';
import { CSVLink } from 'react-csv';
import config from '../../config.json';
import { formatDate, formatShortDate } from '../../utils/dates';
import { profileOnly } from '../../utils/enhacers';
import {
  activatePlayers,
  deleteTeam,
  editarJugador,
  eliminarJugadores,
  hacerCambio,
  traerJugadores,
} from '../../utils/equipos';
import { profilePropType } from '../../utils/proptypes';
import {
  captainContactConflicts,
  playerCsvKeys,
} from '../../utils/playerFields';
import {
  countActive,
  isBlockedByLimit,
  projectActive,
} from '../../utils/playerRules';
import validator from '../../utils/validator';
import CommandHeader from '../CommandHeader';
import Stack from '../Stack';
import TeamForm from '../TeamForm';
import jugadoresForm, {
  CAPTAIN_EMAIL_MESSAGE,
  CAPTAIN_TEL_MESSAGE,
  modelUser,
  validarUsuario,
} from './Forms/Jugadores';

export const computeNewConfig = (data = {}) =>
  data.limitar_cambios && !data.bloquear_cambios
    ? { limitar_cambios: false, bloquear_cambios: true }
    : { limitar_cambios: true, bloquear_cambios: false };

const INITIAL_OPTIONS = [
  { key: 'all', text: 'Todos los jugadores' },
  { key: 'activo', text: 'Activos' },
  { key: 'inactivo', text: 'Inactivos' },
];

export class Players extends Component {
  constructor(props) {
    super(props);
    const {
      team: { cambios = 0 },
    } = props;
    this.selection = new Selection({
      onSelectionChanged: this.onSelectionChanged,
    });
    // Cache for the `columns` getter below, which is why nothing is built here.
    this.columnsCache = null;
    this.columnsAdminFlag = null;
    this.state = {
      selectionCount: null,
      showModal: false,
      showModalTeam: false,
      items: [],
      currentUser: { ...modelUser },
      currentError: {},
      loading: false,
      itemsLoaded: false,
      filtro: 'all',
      puedeCambiar: false,
      bloquearCambios: false,
      limitarCambios: false,
      cambios,
      cambiosDraft: `${cambios}`,
      cambiosError: '',
      cambiosSaving: false,
    };
  }

  async componentDidMount() {
    this.traerJugadores();
    await this.loadCambiosConfigFromDB();
  }

  async componentDidUpdate(prevProps) {
    const { team } = this.props;
    const { team: prevTeam } = prevProps;

    if (prevTeam?.tid !== team?.tid) {
      this.traerJugadores();
      await this.loadCambiosConfigFromDB();
      const { cambios: nextCambios = 0 } = team;
      this.setState({
        cambios: nextCambios,
        cambiosDraft: `${nextCambios}`,
        cambiosError: '',
      });
    }
  }

  componentWillUnmount() {
    firebase.database().ref('limit_changes').off();
  }

  /**
   * Columns read the role flag when asked, so they follow the async profile; the memo keeps Fabric's column identity stable.
   */
  get columns() {
    const adminFlag = !!this.esAdminOsuperAdmin();
    if (!this.columnsCache || this.columnsAdminFlag !== adminFlag) {
      this.columnsAdminFlag = adminFlag;
      this.columnsCache = jugadoresForm(this);
    }
    return this.columnsCache;
  }

  loadCambiosConfigFromDB = async () => {
    const db = firebase.database();
    const ref = db.ref('limit_changes');
    ref.on('value', (snapshot) => {
      const data = snapshot.val() || {
        limitar_cambios: true,
        bloquear_cambios: false,
      };
      this.setState(
        {
          limitarCambios: !!data.limitar_cambios,
          bloquearCambios: !!data.bloquear_cambios,
        },
        () => {
          const puedeCambiar = this.puedeCambiar();
          this.setState({ puedeCambiar });
        },
      );
    });
  };

  traerJugadores = async () => {
    const { team } = this.props;
    const jugadores = await traerJugadores(team);
    const puedeCambiar = this.puedeCambiar();
    this.setState({
      items: jugadores,
      itemsLoaded: true,
      loading: false,
      puedeCambiar,
    });
  };

  esAdminOsuperAdmin = () => {
    const { profile } = this.props;
    return profile.isAdmin || profile.isSuperAdmin;
  };

  eliminarSeleccionados = async () => {
    const { team } = this.props;
    const seleccionados = this.selection.getSelection();
    if (seleccionados.length === 0) return;

    if (!window.confirm(`¿Eliminar ${seleccionados.length} jugador(es)?`)) {
      return;
    }

    const ids = seleccionados.map((jugador) => jugador.identification);
    await eliminarJugadores(team.tid, ids);

    this.traerJugadores();
    this.selection.setAllSelected(false);
  };

  puedeCambiar = () => {
    const { profile } = this.props;
    const { cambios, limitarCambios, bloquearCambios } = this.state;
    if (this.esAdminOsuperAdmin()) {
      return true;
    }
    const limitePermitido = !limitarCambios || cambios < config.CAMBIOS_ETAPA_2;
    const bloqueoGeneral = bloquearCambios || profile.isViewer;
    return limitePermitido && !bloqueoGeneral;
  };

  activar = async (activar = true) => {
    const { team } = this.props;
    const { items } = this.state;
    const isSelected = (identification) =>
      this.selection.isKeySelected(identification);

    const projected = projectActive(items, isSelected, activar);
    this.setState({ items: projected });

    const selected = projected.filter((item) =>
      isSelected(item.identification),
    );
    const { failed } = await activatePlayers(team, selected);

    if (failed.length > 0) {
      // `Promise.allSettled` may report partials: reload from the database, where failed rows stay Inactivo.
      this.traerJugadores();
    }
    this.selection.setAllSelected(false);
  };

  puedeActivar = () => {
    if (this.esAdminOsuperAdmin()) {
      return true;
    }
    const all = this.selection.isAllSelected();
    const jugadores = this.getActivos();
    if (all) {
      return jugadores.inscritos <= config.LIMITE_JUGADORES;
    }
    const { items } = this.state;
    const isSelected = (identification) =>
      this.selection.isKeySelected(identification);
    const nuevosActivos = projectActive(items, isSelected, true).filter(
      (item) => item.activo,
    );
    return nuevosActivos.length <= config.LIMITE_JUGADORES;
  };

  onSelectionChanged = () => {
    const count = this.selection.getSelectedCount();
    this.setState({
      selectionCount: count > 0 && count,
    });
  };

  onItemInvoked = (value) => {
    this.selection.setAllSelected(false);
    const { items } = this.state;
    const currentUser = items.find(
      (item) => item.identification === value.identification,
    );
    this.setState({
      currentUser: {
        ...modelUser,
        ...currentUser,
        // Old Excel-import records may lack `activo` or store it as 1; `!!` keeps the truthiness `getActivos()` counts.
        activo: !!currentUser.activo,
        oldId: currentUser.identification,
        birthday: new Date(currentUser.birthday),
        nuevo: false,
      },
      currentError: {},
      showModal: true,
    });
  };

  onChange = (event) => {
    const { currentUser } = this.state;
    const { value, id } = event.target;
    currentUser[id] = value;
    this.setState({ currentUser });
  };

  guardarCurrent = async () => {
    const { team, profile } = this.props;
    const { currentUser, items, cambios } = this.state;

    const idToCompare = currentUser.oldId
      ? currentUser.oldId
      : currentUser.identification;

    const currentError = await validarUsuario(
      currentUser,
      currentUser.nuevo,
      items
        .filter((item) => item.identification !== idToCompare)
        .map((item) => item.shirt),
      team.tid,
      {
        // Captain from the roster already in state (no extra read); `esSuperAdmin` alone is exempt from the contact block.
        capitan: items.find((item) => item.capitan),
        esAdminOsuperAdmin: this.esAdminOsuperAdmin(),
        esSuperAdmin: !!profile?.isSuperAdmin,
      },
    );
    if (Object.keys(currentError).length > 0) {
      this.setState({ currentError });
      return;
    }

    if (currentUser.nuevo && !this.esAdminOsuperAdmin()) {
      // Re-check against a fresh read before writing; fail closed on an unreadable roster, declined before the spinner.
      let fresh;
      try {
        fresh = await traerJugadores(team);
      } catch (error) {
        fresh = null;
      }
      if (
        !Array.isArray(fresh) ||
        countActive(fresh) >= config.LIMITE_JUGADORES
      ) {
        this.setState({ showModal: false });
        this.traerJugadores();
        return;
      }
    }

    this.setState({ showModal: false, loading: true });
    await editarJugador(currentUser, team);
    if (currentUser.nuevo) {
      await hacerCambio(team, cambios);
      const newCambios = cambios + 1;
      this.setState(
        { cambios: newCambios, cambiosDraft: `${newCambios}` },
        () => {
          const puedeCambiar = this.puedeCambiar();
          this.setState({ puedeCambiar });
        },
      );
    }
    this.traerJugadores();
  };

  onCambiosDraftChange = (event, newValue) => {
    const value =
      newValue !== undefined ? newValue : (event?.target?.value ?? '');
    this.setState({ cambiosDraft: value, cambiosError: '' });
  };

  guardarCambiosCount = async () => {
    const { profile, team } = this.props;
    // SuperAdmin-only write: refuse silently before any validation or state change.
    if (!profile?.isSuperAdmin) return;
    const { cambiosDraft } = this.state;
    const raw = String(cambiosDraft ?? '').trim();
    if (!/^-?\d+$/.test(raw)) {
      this.setState({ cambiosError: 'Ingresa un número válido' });
      return;
    }
    const parsed = parseInt(raw, 10);
    if (!Number.isSafeInteger(parsed)) {
      this.setState({ cambiosError: 'Ingresa un número válido' });
      return;
    }
    this.setState({ cambiosSaving: true });
    try {
      await firebase
        .database()
        .ref(`teams/${team.tid}`)
        .update({ cambios: parsed });
      this.setState(
        {
          cambios: parsed,
          cambiosDraft: `${parsed}`,
          cambiosError: '',
          cambiosSaving: false,
        },
        () => {
          const puedeCambiar = this.puedeCambiar();
          this.setState({ puedeCambiar });
        },
      );
    } catch (error) {
      this.setState({
        cambiosError: 'No se pudo guardar',
        cambiosSaving: false,
      });
    }
  };

  renderCambiosEditor = () => {
    const { profile } = this.props;
    if (!config.LIMITAR_CAMBIOS) return null;
    // The cambios counter is superAdmin-only; fail closed on a missing profile.
    if (!profile?.isSuperAdmin) return null;
    const { cambiosDraft, cambiosError, cambiosSaving } = this.state;
    return (
      <Stack middle end>
        <TextField
          label="Editar cambios"
          value={cambiosDraft}
          onChange={this.onCambiosDraftChange}
          errorMessage={cambiosError}
          type="number"
          styles={{ root: { width: 120 } }}
        />
        <span>&nbsp;&nbsp;</span>
        <PrimaryButton
          onClick={this.guardarCambiosCount}
          disabled={cambiosSaving}
          text="Guardar"
        />
      </Stack>
    );
  };

  /**
   * Live, non-blocking warning counterpart of the contact rule for superAdmin; `{}` for every other role.
   */
  captainContactWarnings = () => {
    const { profile } = this.props;
    if (!profile?.isSuperAdmin) return {};
    const { currentUser, items } = this.state;
    const conflicts = captainContactConflicts({
      player: currentUser,
      captain: items.find((item) => item.capitan),
    });
    return {
      tel: conflicts.tel ? `Advertencia: ${CAPTAIN_TEL_MESSAGE}` : undefined,
      email: conflicts.email
        ? `Advertencia: ${CAPTAIN_EMAIL_MESSAGE}`
        : undefined,
    };
  };

  renderModal = () => {
    const { showModal, currentUser, currentError, items, puedeCambiar } =
      this.state;
    const {
      profile: { isAdmin, isSuperAdmin },
    } = this.props;

    const capitan = items.find((item) => item.capitan);
    const warnings = this.captainContactWarnings();

    return (
      <Modal isOpen={showModal} onDismiss={this.closeModal}>
        <div className="modalContent">
          <Text variant="xLarge">
            {currentUser.nuevo ? 'Agregar Jugador' : 'Editar Jugador'}
          </Text>
          <Stack between className="formModal">
            <div>
              <TextField
                label="Cédula/TI."
                required
                id="identification"
                value={currentUser.identification}
                errorMessage={currentError.identification}
                disabled={!currentUser.nuevo && !isAdmin && !isSuperAdmin}
                onChange={(value) => {
                  this.onChange({
                    ...value,
                    target: {
                      ...value.target,
                      value: validator.toDigits(value.target.value),
                      id: value.target.id,
                    },
                  });
                }}
                type="number"
              />
              <TextField
                label="Primer y segundo nombre"
                required
                onChange={(value) => {
                  this.onChange({
                    ...value,
                    target: {
                      ...value.target,
                      value: value.target.value.toUpperCase(),
                      id: value.target.id,
                    },
                  });
                }}
                id="name"
                value={currentUser.name.toUpperCase()}
                errorMessage={currentError.name}
              />
              <TextField
                label="Primer y segundo apellido"
                required
                onChange={(value) => {
                  this.onChange({
                    ...value,
                    target: {
                      ...value.target,
                      value: value.target.value.toUpperCase(),
                      id: value.target.id,
                    },
                  });
                }}
                id="lastName"
                value={currentUser.lastName.toUpperCase()}
                errorMessage={currentError.lastName}
              />
              <MaskedTextField
                label="Celular"
                required
                mask="(999) 999-9999"
                onChange={(value) => {
                  this.onChange({
                    ...value,
                    target: {
                      ...value.target,
                      value: validator.toDigits(value.target.value),
                      id: value.target.id,
                    },
                  });
                }}
                id="tel"
                type="tel"
                value={currentUser.tel}
                errorMessage={currentError.tel || warnings.tel}
              />
            </div>
            <div>
              <TextField
                label="e-mail"
                required
                onChange={(value) => {
                  this.onChange({
                    ...value,
                    target: {
                      ...value.target,
                      value: value.target.value.toLowerCase(),
                      id: value.target.id,
                    },
                  });
                }}
                id="email"
                value={currentUser.email.toLowerCase()}
                errorMessage={currentError.email || warnings.email}
                type="email"
              />
              <TextField
                label="# Camisa"
                id="shirt"
                value={currentUser.shirt}
                errorMessage={currentError.shirt}
                type="number"
                onChange={(value) => {
                  this.onChange({
                    ...value,
                    target: {
                      ...value.target,
                      value: validator.toDigits(value.target.value),
                      id: value.target.id,
                    },
                  });
                }}
              />
              <DatePicker
                label="Fecha de nacimiento"
                isRequired
                firstDayOfWeek={DayOfWeek.Sunday}
                ariaLabel="Selecciona una fecha"
                maxDate={subYears(new Date(), 6)}
                onSelectDate={(value) =>
                  this.onChange({ target: { value, id: 'birthday' } })
                }
                id="birthday"
                value={
                  typeof currentUser.birthday === 'string'
                    ? new Date(currentUser.birthday)
                    : currentUser.birthday
                }
                errorMessage={currentError.birthday}
              />
              <br />
              {this.esAdminOsuperAdmin() && (
                <Checkbox
                  label="Capitan"
                  isRequired
                  onChange={(value) =>
                    this.onChange({
                      target: { value: value.target.checked, id: 'capitan' },
                    })
                  }
                  boxSide="end"
                  checked={currentUser.capitan || false}
                  errorMessage={currentError.capitan}
                  disabled={
                    capitan &&
                    capitan.identification !== currentUser.identification
                  }
                />
              )}
              {/*
                Admin-only note in the organiser's words. Non-admins never see
                this control, and their save still round-trips the stored value:
                `editarJugador()` writes with `.update()`, which merges rather
                than replaces, so a captain editing anything else cannot blank
                out a comment they were never shown.
              */}
              {this.esAdminOsuperAdmin() && (
                <TextField
                  label="Comentario"
                  id="comentario"
                  value={currentUser.comentario || ''}
                  multiline
                  rows={3}
                  errorMessage={currentError.comentario}
                  onChange={this.onChange}
                />
              )}
            </div>
          </Stack>

          <DialogFooter>
            <PrimaryButton
              disabled={!puedeCambiar}
              onClick={this.guardarCurrent}
              text={currentUser.nuevo ? 'Guardar' : 'Modificar'}
            />
            <DefaultButton onClick={this.closeModal} text="Cancelar" />
          </DialogFooter>
        </div>
      </Modal>
    );
  };

  renderModalTeam = () => {
    const { showModalTeam } = this.state;
    const { team } = this.props;
    return (
      <TeamForm
        showModal={showModalTeam}
        onHideModal={this.closeModalTeam}
        team={team}
      />
    );
  };

  closeModal = () => {
    this.setState({ showModal: false });
  };

  agregarJugador = () => {
    this.setState({
      currentUser: { ...modelUser, nuevo: true },
      currentError: {},
      showModal: true,
    });
  };

  closeModalTeam = () => {
    this.setState({ showModalTeam: false });
  };

  cambiarNombreEquipo = () => {
    this.setState({ showModalTeam: true });
  };

  getActivos = () => {
    const { items } = this.state;
    if (!items) return {};

    const inactivos = items.filter((item) => !item.activo).length;
    return {
      inscritos: items.length,
      inactivos,
      activos: items.length - inactivos,
    };
  };

  eliminarEquipo = () => {
    this.setState({ showEliminarEquipo: true });
  };

  eliminarEquipoConfirmado = async () => {
    const { team } = this.props;
    await deleteTeam(team.tid);
    this.setState({ showEliminarEquipo: false });
    window.location.reload();
  };

  renderEliminarEquipo = () => {
    const { showEliminarEquipo } = this.state;
    return (
      <Dialog
        hidden={!showEliminarEquipo}
        onDismiss={() => {
          this.setState({ showEliminarEquipo: false });
        }}>
        <div className="modalContent">
          <Stack middle between>
            <Text>
              Estás a punto de eliminar un equipo, estos datos no podrán ser
              recuperados ¿Estás seguro de eliminar definitivamente este equipo?
            </Text>
          </Stack>

          <DialogFooter>
            <PrimaryButton
              onClick={this.eliminarEquipoConfirmado}
              text="Eliminar"
            />
            <DefaultButton
              onClick={() => {
                this.setState({ showEliminarEquipo: false });
              }}
              text="Cancelar"
            />
          </DialogFooter>
        </div>
      </Dialog>
    );
  };

  renderCommandHeader = () => {
    const { profile, team } = this.props;
    const { itemsLoaded, puedeCambiar } = this.state;
    // `getActivos()` returns Spanish field names; renamed locally so this module's identifiers stay English.
    const { activos: activeCount } = this.getActivos();
    // Admins are allowed past the limit; captains are not.
    const capBlocked = isBlockedByLimit({
      activeCount,
      limit: config.LIMITE_JUGADORES,
      isAdmin: this.esAdminOsuperAdmin(),
    });

    return (
      <CommandHeader title="Acciones Principales">
        {profile.isAdmin || profile.isSuperAdmin ? (
          <DefaultButton onClick={this.cambiarNombreEquipo}>
            Editar nombre del equipo
          </DefaultButton>
        ) : (
          <Text>{team.name}</Text>
        )}
        <div>
          <PrimaryButton
            onClick={this.agregarJugador}
            // Roster-full and change-allowance are separate questions, so both checks stay.
            disabled={!itemsLoaded || !puedeCambiar || capBlocked}>
            Agregar Jugador
          </PrimaryButton>
          &nbsp;
          {this.esAdminOsuperAdmin() && (
            <DefaultButton onClick={this.eliminarEquipo}>
              Eliminar Equipo
            </DefaultButton>
          )}
          {config.LIMITAR_CAMBIOS && (
            <>
              <br />
              <p className="updateMessage">
                Recuerda que el botón de&nbsp;
                <b>
                  <strong>
                    <i>&ldquo;Agregar jugador&rdquo;</i>
                  </strong>
                </b>
                &nbsp; estará deshabilitado para realizar cambios en los
                horarios según el reglamento y socializados en el congreso
                técnico. Si requieres cambios por incapacidad, repórtalos a
                soporte@torneosoccercup.com
              </p>
            </>
          )}
        </div>
      </CommandHeader>
    );
  };

  renderContent = () => {
    const { items, loading, itemsLoaded } = this.state;
    return (
      <>
        <div className="teams">
          {this.renderTopContent()}
          {itemsLoaded && items.length === 0 ? (
            <div className="dialogBox">
              <Text variant="xxLarge">Lista sin jugadores</Text>
            </div>
          ) : (
            this.renderTable()
          )}
        </div>
        {loading && <ProgressIndicator />}
      </>
    );
  };

  renderFilter = () => {
    const { team } = this.props;
    const esAdminOsuperAdmin = this.esAdminOsuperAdmin();
    // Only an admin or a superAdmin gets the filter and the CSV download.
    if (!esAdminOsuperAdmin) return null;

    const { items, filtro } = this.state;
    const itemsFiltered =
      filtro === 'all'
        ? items
        : items.filter((item) => {
            if (item.activo && filtro === 'activo') {
              return true;
            }
            if (!item.activo && filtro === 'inactivo') {
              return true;
            }
            return false;
          });

    // Shared with `DownloadAll`; the role gate stays as defense in depth behind the early return.
    const csvKeys = playerCsvKeys({
      modelKeys: Object.keys(modelUser),
      esAdminOsuperAdmin,
    });

    const csvData = itemsFiltered.reduce(
      (chain, value) => {
        const values = csvKeys.map((key) => {
          if (key === 'fullName') {
            return `${value.name || ''} ${value.lastName || ''}`.trim();
          }
          if (key === 'updatedOn') {
            return value[key] ? formatDate(value[key]) : '';
          }

          return key === 'birthday' ? formatShortDate(value[key]) : value[key];
        });
        return [...chain, values];
      },
      [csvKeys],
    );
    return (
      <>
        <Stack between>
          <div />
          <Stack middle end>
            <ComboBox
              defaultSelectedKey="all"
              label="Filtrar por:"
              options={INITIAL_OPTIONS}
              onChange={(e, { key }) => this.setState({ filtro: key })}
            />
            <span>&nbsp;&nbsp;</span>
            <CSVLink data={csvData} filename={`${team.name}-soccer-cup.csv`}>
              <PrimaryButton>Descargar listado</PrimaryButton>
            </CSVLink>
          </Stack>
        </Stack>
        <br />
      </>
    );
  };

  renderTopContent = () => {
    const { selectionCount, items, itemsLoaded, cambios } = this.state;
    const jugadores = this.getActivos();
    // Interpolated as stored, no clamp: a negative allowance must show its debt, not a reassuring zero.
    const textoCambios = config.LIMITAR_CAMBIOS
      ? ` Llevas ${cambios}/${config.CAMBIOS_ETAPA_2} de los cambios permitidos`
      : '';

    const jugadoresActivos = jugadores.inscritos - jugadores.inactivos;
    const { profile } = this.props;
    return (
      <div className="top">
        <h4>Listado de Jugadores</h4>
        <Shimmer isDataLoaded={itemsLoaded}>
          {items.length > 0 && (
            <>
              {this.renderFilter()}
              <Stack between middle className="teamsTopBar">
                <div>
                  <Text>
                    {jugadores.inscritos} jugadores inscritos:&nbsp;
                    {jugadoresActivos} activo(s) -&nbsp;
                    {jugadores.inactivos} inactivo(s).{textoCambios}
                  </Text>
                  {this.renderCambiosEditor()}
                </div>
                <Stack middle>
                  <>
                    <Text>Acciones&nbsp;&nbsp;</Text>
                    <PrimaryButton
                      onClick={() => this.activar()}
                      disabled={!this.puedeActivar() || !this.puedeCambiar()}>
                      Activar {selectionCount}
                    </PrimaryButton>
                    &nbsp;
                    <DefaultButton
                      onClick={() => this.activar(false)}
                      disabled={!this.puedeCambiar()}>
                      Desactivar {selectionCount}
                    </DefaultButton>
                    &nbsp;
                    {profile.isSuperAdmin && (
                      <DefaultButton
                        onClick={this.eliminarSeleccionados}
                        disabled={this.selection.getSelectedCount() === 0}>
                        Eliminar seleccionados
                      </DefaultButton>
                    )}
                    &nbsp;
                  </>
                </Stack>
              </Stack>
            </>
          )}
        </Shimmer>
      </div>
    );
  };

  renderTable = () => {
    const { items, itemsLoaded, filtro } = this.state;
    const itemsFiltered =
      filtro === 'all'
        ? items
        : items.filter((item) => {
            if (item.activo && filtro === 'activo') {
              return true;
            }
            if (!item.activo && filtro === 'inactivo') {
              return true;
            }
            return false;
          });
    return (
      <MarqueeSelection selection={this.selection}>
        <ShimmeredDetailsList
          items={itemsFiltered.map((item) => ({
            ...item,
            key: item.identification,
            estado: item.activo ? (
              <span className="inscrito">Activo</span>
            ) : (
              <span className="noInscrito">Inactivo</span>
            ),
          }))}
          columns={this.columns}
          setKey="set"
          layoutMode={DetailsListLayoutMode.fixedColumns}
          selection={this.selection}
          selectionPreservedOnEmptyClick
          ariaLabelForSelectionColumn="Toggle selection"
          ariaLabelForSelectAllCheckbox="Toggle selection for all items"
          onItemInvoked={this.onItemInvoked}
          enableShimmer={!itemsLoaded}
        />
      </MarqueeSelection>
    );
  };

  render() {
    return (
      <>
        {this.renderCommandHeader()}
        {this.renderContent()}
        {this.renderModal()}
        {this.renderModalTeam()}
        {this.renderEliminarEquipo()}
      </>
    );
  }
}

Players.propTypes = {
  team: PropTypes.shape({
    name: PropTypes.string.isRequired,
    cambios: PropTypes.number,
    tid: PropTypes.string.isRequired,
  }).isRequired,
  profile: profilePropType.isRequired,
};

Players.defaultProps = {};

export default profileOnly(Players);
